import { createContext, useContext } from "react";
import Accordion from "react-bootstrap/Accordion";
import style from "./PreEnrichmentRuns.module.css";

export const CollapsibleLists = createContext(false);

const ListAccordion = ({ title, count, children }) => (
  <Accordion>
    <Accordion.Item eventKey="list">
      <Accordion.Header>{title} ({count})</Accordion.Header>
      <Accordion.Body>{children}</Accordion.Body>
    </Accordion.Item>
  </Accordion>
);

export const label = key => key.replaceAll("_", " ").replace(/\b\w/g, letter => letter.toUpperCase());

const fieldNames = {
  "document.ingestion_status": "Document ingestion status",
  "document.extraction.status": "Content extraction status",
  "document.enrichment.content_fingerprint": "Content fingerprint",
  "document.archive.archived": "Document archived",
  "document.metadata.quarantine_reason": "Quarantine reason",
  "document.extension": "File extension",
};
const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const readableField = field => fieldNames[field] || label(String(field).replace(/^document\./, "").replaceAll(".", " "));
const EvidenceValue = ({ value }) => {
  const collapsible = useContext(CollapsibleLists);
  if (collapsible && Array.isArray(value)) return <DetailValue value={value} title="Values" />;
  if (typeof value === "string" && /^[A-Z][A-Z_]+$/.test(value)) return label(value.toLowerCase());
  if (Array.isArray(value)) return value.length ? value.map(item => typeof item === "string" ? item.replaceAll("_", " ") : JSON.stringify(item)).join(", ") : "None";
  return <DetailValue value={value} />;
};

const Evidence = ({ evidence }) => {
  const collapsible = useContext(CollapsibleLists);
  const content = (
  <div>
    <ol className={style.conditions}>
      {evidence.conditions.map((condition, index) => {
        if (!condition || typeof condition !== "object" || Array.isArray(condition)) return <li key={index}><DetailValue value={condition} /></li>;
        const unary = ["IS_NULL", "NOT_NULL"].includes(condition.operator);
        const remaining = Object.fromEntries(Object.entries(condition).filter(([key]) => !["field", "operator", "expected", "value", "exists"].includes(key)));
        return <li key={index} className={style.condition}>
          <div className={style.conditionTitle}>Condition {index + 1}</div>
          {condition.field && <strong title={condition.field}>{readableField(condition.field)}</strong>}
          {condition.operator && <div className={style.comparison}>
            <span>Requirement:</span>{" "}
            <strong>{String(condition.operator).replaceAll("_", " ").toUpperCase()}</strong>{" "}
            {!unary && has(condition, "expected") && <EvidenceValue value={condition.expected} />}
          </div>}
          {!condition.operator && has(condition, "expected") && <div>Expected: <EvidenceValue value={condition.expected} /></div>}
          {has(condition, "value") && <div>Actual value: <EvidenceValue value={condition.value} /></div>}
          {has(condition, "exists") && <div>Field present: {condition.exists ? "Yes" : "No"}</div>}
          {Object.keys(remaining).length > 0 && <DetailFields data={remaining} />}
        </li>;
      })}
    </ol>
    {evidence.conditions.length === 0 && <p>No conditions recorded.</p>}
    <DetailFields data={Object.fromEntries(Object.entries(evidence).filter(([key]) => key !== "conditions"))} empty={null} />
  </div>
  );
  return collapsible ? <ListAccordion title="Evidence" count={evidence.conditions.length}>{content}</ListAccordion> : content;
};

// Render every response field, including nested fields and future additions.
export const DetailValue = ({ value, title = "Items" }) => {
  const collapsible = useContext(CollapsibleLists);
  if (value === null || value === undefined) return "Not specified";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    const content = value.length ? <ul>{value.map((item, index) => <li key={index} className={item?.rule_id ? style.rule : undefined}>
    {item?.rule_id && <div className={style.conditionTitle}>{item.name || `Rule ${index + 1}`}{typeof item.matched === "boolean" ? ` — ${item.matched ? "Matched" : "Not matched"}` : ""}</div>}
    <DetailValue value={item} />
    </li>)}</ul> : "None";
    return collapsible ? <ListAccordion title={title} count={value.length}>{content}</ListAccordion> : content;
  }
  if (typeof value === "object") return <DetailFields data={value} />;
  return String(value);
};

export const DetailFields = ({ data, empty = "None", renderField }) => Object.keys(data).length ? (
  <dl className={style.details}>
    {Object.entries(data).map(([key, value]) => (
      <div key={key}>
        <dt>{label(key)}</dt>
        <dd>{renderField?.(key, value) ?? (key === "evidence" && Array.isArray(value?.conditions) ? <Evidence evidence={value} /> : <DetailValue value={value} title={label(key)} />)}</dd>
      </div>
    ))}
  </dl>
) : empty;
